<template>
  <div class="feedback">
    <a @click="openModal" class="feedback__open btn btn-sm btn-outline-secondary feedbackbtn">
      {{ submitModalButtonText }}
    </a>

    <b-modal-custom title="Send feedback"
                    class="text-white"
                    :modal-class.sync="$options.data.themeClass"
                    v-model="modalDisplay"
                    @ok="submitModal"
                    @cancel="resetForm">
      <div v-if="feedbackIsSending" class="text-center">
        <i class="fa fa-5x fa-cog fa-spin text-muted m-2"></i>
      </div>
      <div v-else>
        <div v-if="screenshotAvailable" class="feedback__screenshot-switcher">
          <span>Attach screenshot</span>

          <!-- note: coreui v3 CSwitch issue, v-model doesn't work -->
          <CSwitch :checked.sync="screenshotEnabled"
                   v-bind="switcherLabel"
                   class="mx-1" color="dark" variant="outline" shape="pill" size="sm"/>
        </div>
        <p v-if="screenshotEnabled" class="feedback__screenshot">
          <img :src="dataURLScreenshot" alt="screenshot"/>
        </p>

        <b-form>
          <b-form-group
            description="Let us know your full name"
            label="Enter your name"
            label-for="name"
            :label-cols="3">
            <b-form-input
              id="name"
              type="text"
              autocomplete="name"
              required
              v-model.trim="$v.form.name.$model"
              :state="checkState('name')"/>

            <b-form-invalid-feedback id="name_feedback">
              This is a required field and must be at least 5 characters
            </b-form-invalid-feedback>
          </b-form-group>

          <b-form-group
            description="Please enter your email"
            label="Email"
            label-for="email"
            :label-cols="3">
            <b-form-input
              id="email"
              type="email"
              placeholder="Enter your email"
              autocomplete="email"
              required
              v-model.trim="$v.form.email.$model"
              :state="checkState('email')"/>

            <b-form-invalid-feedback id="email_feedback">
              This is a required field, must be a valid email address
            </b-form-invalid-feedback>
          </b-form-group>

          <b-form-group
            description="Please provide a subject"
            label="Subject"
            label-for="subject"
            :label-cols="3">
            <b-form-input
              id="subject"
              type="text"
              placeholder="Text"
              required
              v-model.trim="$v.form.subject.$model"
              :state="checkState('subject')"/>

            <b-form-invalid-feedback id="subject_feedback">
              This is a required field and must be at least 10 characters
            </b-form-invalid-feedback>
          </b-form-group>

          <b-form-group
            label="Details"
            label-for="body"
            :label-cols="3">
            <b-form-textarea
              id="body"
              :rows="9"
              placeholder="Details.."
              required
              v-model.trim="$v.form.body.$model"
              :state="checkState('body')"/>

            <b-form-invalid-feedback id="body_feedback">
              This is a required field and must be at least 10 characters
            </b-form-invalid-feedback>
          </b-form-group>
        </b-form>
      </div>

      <template v-if="feedbackIsSending" v-slot:modal-footer>
        <!-- disable footer when feedback is sending -->
        <span></span>
      </template>

    </b-modal-custom>

  </div>
</template>

<script>
import { mapActions } from 'vuex';
import html2canvas from 'html2canvas';
import { validationMixin } from 'vuelidate';
import { required, minLength, email } from 'vuelidate/lib/validators';
import miniToastr from 'mini-toastr';
import BModalCustom from '@/base_components/BModalCustom';

const defaultFields = {
  name: '',
  email: '',
  subject: '',
  body: '',
};

export default {
  name: 'Feedback',
  mixins: [validationMixin],
  components: {BModalCustom},
  data: function () {
    return {
      modalDisplay: false,
      form: Object.assign({}, defaultFields),
      dataURLScreenshot: null,
      blobScreenshot: null,
      screenshotEnabled: true,

      switcherLabel: {
        labelOn: 'yes',
        labelOff: 'no'
      },
      feedbackIsSending: false,
      submitModalButtonText: 'Submit feedback',
    };
  },

  mounted() {
    miniToastr.init();
  },

  methods: {
    ...mapActions(
      'api', ['getUser']
    ),

    ...mapActions(
      'api/feedback', ['sendFeedback']
    ),

    closeModal: function () {
      this.modalDisplay = false;
      this.resetForm();
    },

    openModal: async function () {
      this.feedbackIsSending = false;
      this.submitModalButtonText = 'Submit feedback..';

      this.blobScreenshot = await this.getScreenshot();

      const user = await this.getUser();
      this.form.name = `${user.name} (${user.nickname})`;
      this.form.email = user.email;
      this.modalDisplay = true;
      this.submitModalButtonText = 'Submit feedback';
    },

    submitModal: function (ev) {
      ev.preventDefault();
      if(this.validate()) {
        this.$nextTick(() => {
          this.feedbackIsSending = true;

          let formData = new FormData();
          if(this.screenshotEnabled) {
            // if screenshot switcher is toggled on
            formData.append('file', this.blobScreenshot, 'screenshot.png');
            formData.append('fileSent', true)
          } else {
            formData.append('fileSent', false)

          }
          Object.keys(this.form).forEach((fieldName) => {
            formData.append(fieldName, this.form[fieldName]);
          });

          this.sendFeedback(formData).then(result => {
            this.modalDisplay = false;
            this.resetForm();
            if(result) {
              miniToastr.success('Your request was submitted successfully', null, 5000);
            } else {
              miniToastr.error(
                'An error occured during your request submitting, please contact us', null, 5000
              );
            }
          });
        });
      }
    },

    async getScreenshot() {
      let canvas = null;
      try {
        canvas = await html2canvas(document.body);
      } catch(e) {
        console.log('can\'t get a screenshot', e);
        this.screenshotEnabled = false;
        return null;
      }

      this.dataURLScreenshot = canvas.toDataURL();
      return await new Promise(
        resolve => canvas.toBlob(resolve, 'image/png'),
      );
    },

    resetForm() {
      // Reset validation
      this.form = Object.assign({}, defaultFields);
      // this.submitted = false;
      this.$nextTick(() => {
        this.$v.$reset();
      })
    },

    checkState(val) {
      const field = this.$v.form[val];
      return !field.$dirty || !field.$invalid;
    },

    findFirstError(component = this) {
      if(component.state === false) {
        if(component.$refs.input) {
          component.$refs.input.focus();
          return true;
        }
        if(component.$refs.check) {
          component.$refs.check.focus();
          return true;
        }
      }
      let focused = false;
      component.$children.some((child) => {
        focused = this.findFirstError(child);
        return focused
      });

      return focused;
    },

    validate() {
      this.$v.$touch();
      this.$nextTick(() => this.findFirstError());
      return this.isValid;
    }
  },

  computed: {
    formStr() {
      return JSON.stringify(this.form, null, 4)
    },

    isValid() {
      return !this.$v.form.$anyError
    },

    isDirty() {
      return this.$v.form.$anyDirty
    },

    screenshotAvailable() {
      return !!this.blobScreenshot;
    },
  },

  validations: {
    form: {
      name: {
        required,
        minLength: minLength(5),
      },
      email: {
        required,
        email,
      },
      subject: {
        required,
        minLength: minLength(10),
      },
      body: {
        required,
        minLength: minLength(10),
      },
    }
  },
}
</script>

<style scoped lang="scss">
.feedbackbtn {
  background-color: #0079c6;
  border: 0 solid rgb(121, 121, 121);
  color: white;
}

.feedback {
  &__open {
    cursor: pointer;
    min-width: 130px;
  }

  &__screenshot {
    padding: 20px;
    border: 1px solid rgb(121, 121, 121);
    text-align: center;

    img {
      max-height: 300px;
      max-width: 100%;
    }
  }

  &__screenshot-switcher {
    margin-bottom: 10px;
    display: flex;
    align-items: center;
    justify-content: flex-end;

    span {
      margin-right: 5px;
    }
  }
}
</style>
